import React from 'react';
import { withTooltip } from '../../hoc';
import { IconButton as ChakraUiIconButton } from '@chakra-ui/react';
import { ButtonProps, ButtonSize } from '../Button';
import { Icon as CoreIcon, IconProps as CoreIconProps } from '../Icon';

interface Props extends ButtonProps {
  ariaLabel: string;
  icon: CoreIconProps;
  isRound?: boolean;
  colorScheme?: string;
}

// `compact` is a real size in the button recipe (32px). It is deliberately absent here so it
// reaches the recipe rather than being folded into `2xs`, which is a different size (24px).
const ICON_BUTTON_SIZE_MAP: Partial<Record<ButtonSize, '2xs' | 'xs' | 'sm' | 'md' | 'lg'>> = {
  [ButtonSize['2XS']]: '2xs',
  [ButtonSize.XS]: 'xs',
  [ButtonSize.SM]: 'sm',
  [ButtonSize.MD]: 'md',
  [ButtonSize.LG]: 'lg',
};

export const IconButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    icon,
    ariaLabel,
    isActive,
    active,
    size,
    variant,
    isFullWidth,
    isDisabled,
    disabled,
    isInvert,
    colorScheme,
    colorPalette,
    isRound,
    tooltip,
    ...rest
  } = props;

  // Chakra's `_active` matches `[data-active]` by presence — false must omit the attribute, not set "false".
  const activeAttr = (isActive ?? active) ? { 'data-active': '' } : undefined;

  /**
   * DOM
   */
  return (
    <ChakraUiIconButton
      data-object-type="core-icon-button"
      ref={ref}
      {...rest}
      aria-label={ariaLabel}
      {...activeAttr}
      {...(isFullWidth && { width: 'full', flex: '1' })}
      {...(isRound && { borderRadius: 'full' })}
      size={size ? ((ICON_BUTTON_SIZE_MAP[size] ?? size) as never) : undefined}
      variant={variant as never}
      disabled={isDisabled ?? disabled}
      invert={isInvert as never}
      colorPalette={colorScheme ?? colorPalette}
    >
      <CoreIcon {...icon} />
    </ChakraUiIconButton>
  );
});
IconButton.displayName = 'IconButton';

export default withTooltip<HTMLButtonElement>('IconButton')(IconButton);
